import { useCallback, useMemo, useState, type ComponentProps } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import { useDebouncedValue } from "../../../lib/useDebouncedValue";
import { Button } from "../../../app/components/ui/button";
import { Badge } from "../../../app/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/ui/select";
import { SalesDetailDrawer } from "../components/SalesDetailDrawer";

type SaleRow = Awaited<ReturnType<typeof api.sales.list>>["data"][number];

function formatMoney(amount: string | number | null | undefined) {
  const n = typeof amount === "string" ? Number(amount) : typeof amount === "number" ? amount : 0;
  return `₹${n.toFixed(2)}`;
}

function formatDate(iso: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function statusBadgeVariant(status: string): ComponentProps<typeof Badge>["variant"] {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "completed") return "default";
  if (s === "cancelled") return "destructive";
  if (s === "refunded") return "secondary";
  return "outline";
}

export function SalesPage() {
  const { token } = useAuth();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 350);

  const [activeSaleId, setActiveSaleId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const queryKey = useMemo(
    () => [
      "sales",
      {
        page,
        perPage,
        dateFrom,
        dateTo,
        paymentMethod,
        status,
        q: debouncedQ.trim(),
      },
    ],
    [page, perPage, dateFrom, dateTo, paymentMethod, status, debouncedQ],
  );

  const listQuery = useQuery({
    queryKey,
    enabled: Boolean(token),
    queryFn: async () => {
      if (!token) throw new Error("Not authenticated");
      return api.sales.list({
        token,
        query: {
          page,
          per_page: perPage,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          search: debouncedQ.trim() || undefined,
          payment_method: paymentMethod !== "all" ? paymentMethod : undefined,
          status: status !== "all" ? status : undefined,
        },
      });
    },
  });

  const rows = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta ?? { current_page: 1, last_page: 1, per_page: perPage, total: 0 };

  const openDetail = useCallback((id: string) => {
    setActiveSaleId(id);
    setDrawerOpen(true);
  }, []);

  const downloadInvoice = useCallback(
    async (sale: SaleRow) => {
      if (!token) return;
      try {
        const blob = await api.sales.downloadInvoice({ token, id: sale.id });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice_${sale.invoice_number ?? sale.id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to download invoice");
      }
    },
    [token],
  );

  const printInvoice = useCallback(
    async (sale: SaleRow) => {
      if (!token) return;
      try {
        const blob = await api.sales.downloadInvoice({ token, id: sale.id });
        const url = window.URL.createObjectURL(blob);
        const w = window.open(url, "_blank");
        if (!w) {
          toast.message("Popup blocked. Please allow popups to print.");
          return;
        }
        // Best-effort: wait for the PDF viewer to load, then call print.
        window.setTimeout(() => {
          try {
            w.focus();
            w.print();
          } catch {
            // ignore
          }
        }, 600);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to open invoice for printing");
      }
    },
    [token],
  );

  const exportExcel = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.sales.exportSalesReport({
        token,
        query: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          search: debouncedQ.trim() || undefined,
          payment_method: paymentMethod !== "all" ? paymentMethod : undefined,
          status: status !== "all" ? status : undefined,
        },
      });
      const url = window.URL.createObjectURL(res.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Sales report downloaded.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to export sales report");
    }
  }, [token, dateFrom, dateTo, debouncedQ, paymentMethod, status]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Sales</h1>
          <p className="text-muted-foreground">Review sales history and download invoices.</p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Button
            type="button"
            variant="outline"
            className="min-h-9 w-full sm:w-auto"
            onClick={() => void exportExcel()}
            disabled={!token}
          >
            Export sales report (Excel)
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All sales</CardTitle>
          <CardDescription>Filter by date, payment method, status, or search invoice/customer.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-end">
              <div className="min-w-0 space-y-1">
                <div className="text-xs text-muted-foreground">From</div>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="min-h-9 w-full min-w-0 sm:w-[170px]"
                />
              </div>
              <div className="min-w-0 space-y-1">
                <div className="text-xs text-muted-foreground">To</div>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="min-h-9 w-full min-w-0 sm:w-[170px]"
                />
              </div>

              <div className="col-span-2 min-w-0 space-y-1 sm:col-span-1">
                <div className="text-xs text-muted-foreground">Payment</div>
                <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v)}>
                  <SelectTrigger className="min-h-9 w-full min-w-0 sm:w-[160px]">
                    <SelectValue placeholder="Payment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="gpay">GPay</SelectItem>
                    <SelectItem value="razorpay">Razorpay</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 min-w-0 space-y-1 sm:col-span-1">
                <div className="text-xs text-muted-foreground">Status</div>
                <Select value={status} onValueChange={(v) => setStatus(v)}>
                  <SelectTrigger className="min-h-9 w-full min-w-0 sm:w-[160px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
              <Input
                placeholder="Search invoice/customer phone..."
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                className="min-h-9 w-full min-w-0 sm:max-w-[280px]"
              />
              <Button
                type="button"
                variant="outline"
                className="min-h-9 w-full sm:w-auto"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                  setPaymentMethod("all");
                  setStatus("all");
                  setQ("");
                  setPage(1);
                }}
              >
                Reset
              </Button>
            </div>
          </div>

          {listQuery.isError ? (
            <div className="text-sm text-destructive">
              {listQuery.error instanceof Error ? listQuery.error.message : "Failed to load sales."}
            </div>
          ) : null}

          <div className="min-w-0 overflow-x-auto rounded-md border md:border-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice ID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">Tax</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="min-w-0 text-right md:min-w-[12rem]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-muted-foreground">
                    Loading sales...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-muted-foreground">
                    No sales found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer"
                    onClick={() => openDetail(String(s.id))}
                  >
                    <TableCell className="font-medium">{s.invoice_number ?? `#${s.id}`}</TableCell>
                    <TableCell>{formatDate(s.created_at)}</TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <div className="font-medium">{s.customer_name ?? "-"}</div>
                        <div className="text-xs text-muted-foreground">{s.customer_phone ?? "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{s.items_count}</TableCell>
                    <TableCell className="text-right">{formatMoney(s.subtotal)}</TableCell>
                    <TableCell className="text-right">{formatMoney(s.tax_amount)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatMoney(s.total_amount)}</TableCell>
                    <TableCell>{(s.payment_method ?? "").toUpperCase()}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(s.status)}>{(s.status ?? "").toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                        <Button size="sm" variant="outline" className="min-h-9" onClick={() => openDetail(String(s.id))}>
                          View details
                        </Button>
                        <Button size="sm" variant="outline" className="min-h-9" onClick={() => void downloadInvoice(s)}>
                          Download
                        </Button>
                        <Button size="sm" variant="secondary" className="min-h-9" onClick={() => void printInvoice(s)}>
                          Print
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Page {meta.current_page} of {meta.last_page} · {meta.total} total
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
                <SelectTrigger size="sm" className="min-h-9 w-full min-w-0 sm:w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={meta.current_page <= 1 || listQuery.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={meta.current_page >= meta.last_page || listQuery.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <SalesDetailDrawer
        open={drawerOpen}
        onOpenChange={(v) => {
          setDrawerOpen(v);
          if (!v) setActiveSaleId(null);
        }}
        saleId={activeSaleId}
      />
    </div>
  );
}

